import { injectGlobal, css, cx } from "@emotion/css";
import { text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ReactNode } from "react";
import { Tooltip as TippyTooltip } from "react-tippy";
import "react-tippy/dist/tippy.css";
export type TooltipContent = string | ReactNode;
export type TooltipPosition = "right" | "left" | "top" | "bottom";
export interface TooltipProps {
    /**
     * Sets the class name of the div wrapper.
     * @deprecated Please avoid using this. This property will be removed in the near future.
     */
    className?: string;
    /** The content to be shown in the tooltip popper */
    content?: TooltipContent;
    /** The html element that will show a tooltip when user hovers over it */
    children?: React.ReactNode;
    /** The position that the tooltip popper shows based on the container element (children prop)*/
    position?: TooltipPosition;
    /**
     * Specifies which type of events will trigger a tooltip to show
     * More info can be found: https://github.com/tvkhoa/react-tippy
     */
    trigger?: "mouseenter" | "focus" | "click" | "manual";
    /** Just only use it if you want to show/hide tooltip popper manually. Usually, you don't need it*/
    open?: boolean;
    /**
     * Specifies how far away the tooltip is from its element, in pixel.
     * @deprecated Please avoid using this, and please contact BPE team if you really need this, it could be a good signal to improve our Tooltip component.
     */
    distance?: number;
    /**
     * Controls the display css property of the tooltip div.
     */
    display?: "block" | "inline-block" | "flex" | "inline";
    /**
     * Set to true when tooltip has Markdown content.
     * This applies inverse colors to markdown elements for better visibility.
     */
    isContentMarkdown?: boolean;
}
export function Tooltip({ content, className, children, position = "top", open, trigger, distance, display, isContentMarkdown = false }: TooltipProps) {
    const contentClassName = cx(tooltipContentStyles, isContentMarkdown && markdownTooltipStyles);
    return (<TippyTooltip className={cx(tooltipContainerStyles, className)} open={open} trigger={trigger} arrow={true} position={position} useContext={true} html={<span className={contentClassName}>{content}</span>} size="small" style={{ display: display ?? "inline-block" }} distance={distance} popperOptions={{
            modifiers: {
                preventOverflow: { boundariesElement: "window" },
            },
        }}>
            {children}
        </TippyTooltip>);
}
const tooltipContainerStyles = css({
    maxWidth: "100%",
    // react-tippy adds an empty div element immediately after the container when the useContext prop is true, the empty div causes extra space or line when the container is hovered,
    // therefore using the adjacent sibling selector here to remove the side effect.
    "+ div:empty": {
        display: "none",
    },
});
const tooltipContentStyles = css({
    backgroundColor: themeTokens.color.background.inverse.primary,
    color: themeTokens.color.text.inverse.primary,
    font: text.body.regular.xSmall,
    maxWidth: "400px", // this value is coming from tippy internal styles
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
});
// When Tooltip renders markdown, the Markdown component creates <p>, <h1..6> and <li> tags. The style above is overridden by the markdown css,
// so adding following block to apply our custom css when the tooltip content is using markdown.
const markdownTooltipStyles = css({
    "p, h1, h2, h3, h4, h5, h6, li": {
        backgroundColor: themeTokens.color.background.inverse.primary,
        color: themeTokens.color.text.inverse.primary,
    },
});
// There is no way to gain more granular control of popper styles other than to leverage the class names attached to tippy-tooltip elements
injectGlobal({
    ".tippy-popper .tippy-tooltip": {
        backgroundColor: themeTokens.color.background.inverse.primary,
        // Globally change text to follow the background, so that we "play nice with others" on the same page
        color: themeTokens.color.text.inverse.primary,
    },
    ".tippy-popper[x-placement^=\"right\"] .arrow-regular[x-arrow]": {
        borderRightColor: themeTokens.color.background.inverse.primary,
    },
    ".tippy-popper[x-placement^=\"left\"] .arrow-regular[x-arrow]": {
        borderLeftColor: themeTokens.color.background.inverse.primary,
    },
    ".tippy-popper[x-placement^=\"top\"] .arrow-regular[x-arrow]": {
        borderTopColor: themeTokens.color.background.inverse.primary,
    },
    ".tippy-popper[x-placement^=\"bottom\"] .arrow-regular[x-arrow]": {
        borderBottomColor: themeTokens.color.background.inverse.primary,
    },
});
